'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { type FrontNotificationPayload, NOTIFICATIONS_REALTIME_TOPICS, REALTIME_CHANNELS } from '@repo/contracts/realtime';

import { useAppContext } from '@context/AppContext';
import { useRealtimeChannel } from '@context/RealtimeContext';

import myFetch from '@utils/myFetch';

type FrontNotificationsContextValue = {
    unreadCount: number;
    latest: FrontNotificationPayload[];
    latestUnread: FrontNotificationPayload[];
    loading: boolean;
    error: Error | null;
    refreshSummary: () => Promise<void>;
    markAsRead: (recipientId: string) => Promise<FrontNotificationPayload>;
    markAsUnread: (recipientId: string) => Promise<FrontNotificationPayload>;
    markAllAsRead: () => Promise<void>;
};

type FrontNotificationsProviderProps = {
    children: React.ReactNode;
};

type NotificationSummaryResponse = {
    unreadCount?: number;
    latest?: FrontNotificationPayload[];
    latestUnread?: FrontNotificationPayload[];
};

const defaultSummary = {
    unreadCount: 0,
    latest: [],
    latestUnread: [],
};

const FrontNotificationsContext = createContext<FrontNotificationsContextValue | null>(null);

export function FrontNotificationsProvider({ children }: FrontNotificationsProviderProps): React.JSX.Element {
    const [user] = useAppContext('user');
    const [summary, setSummary] = useState<{ unreadCount: number; latest: FrontNotificationPayload[]; latestUnread: FrontNotificationPayload[] }>(defaultSummary);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const refreshSummary = useCallback(async (): Promise<void> => {
        if (!user?._id) {
            setSummary(defaultSummary);
            setLoading(false);
            setError(null);
            return;
        }

        setLoading(true);

        try {
            const response = (await myFetch.get('notifications/summary')) as NotificationSummaryResponse;
            const latestUnread = Array.isArray(response?.latestUnread) ? response.latestUnread : [];
            setSummary({
                unreadCount: Number(response?.unreadCount || 0),
                latest: Array.isArray(response?.latest) ? response.latest : latestUnread,
                latestUnread,
            });
            setError(null);
        } catch (nextError) {
            setError(nextError instanceof Error ? nextError : new Error('NOTIFICATIONS_SUMMARY_FAILED'));
        } finally {
            setLoading(false);
        }
    }, [user?._id]);

    const markAsRead = useCallback(async (recipientId: string): Promise<FrontNotificationPayload> => {
        const response = (await myFetch.patch(`notifications/${recipientId}/read`, {})) as FrontNotificationPayload;
        void refreshSummary();
        return response;
    }, [refreshSummary]);

    const markAsUnread = useCallback(async (recipientId: string): Promise<FrontNotificationPayload> => {
        const response = (await myFetch.patch(`notifications/${recipientId}/unread`, {})) as FrontNotificationPayload;
        void refreshSummary();
        return response;
    }, [refreshSummary]);

    const markAllAsRead = useCallback(async (): Promise<void> => {
        await myFetch.patch('notifications/read-all', {});
        void refreshSummary();
    }, [refreshSummary]);

    const processPendingPositioningResponses = useCallback(async (): Promise<void> => {
        if (!user?._id) {
            return;
        }
        try {
            await myFetch.post('shared-links/positioning-responses/process', {});
        } catch {
            // Best effort: the backend lock keeps processing idempotent, and failures are retried later.
        }
    }, [user?._id]);

    useEffect(() => {
        void refreshSummary();
        void processPendingPositioningResponses();
    }, [refreshSummary, processPendingPositioningResponses]);

    useRealtimeChannel({
        channel: REALTIME_CHANNELS.notifications,
        topic: NOTIFICATIONS_REALTIME_TOPICS.created,
        enabled: Boolean(user?._id),
        onMessage: () => {
            void processPendingPositioningResponses();
            void refreshSummary();
        },
    });

    useRealtimeChannel({
        channel: REALTIME_CHANNELS.notifications,
        topic: NOTIFICATIONS_REALTIME_TOPICS.read,
        enabled: Boolean(user?._id),
        onMessage: () => {
            void refreshSummary();
        },
    });

    useRealtimeChannel({
        channel: REALTIME_CHANNELS.notifications,
        topic: NOTIFICATIONS_REALTIME_TOPICS.unread,
        enabled: Boolean(user?._id),
        onMessage: () => {
            void refreshSummary();
        },
    });

    const value = useMemo<FrontNotificationsContextValue>(() => ({
        unreadCount: summary.unreadCount,
        latest: summary.latest,
        latestUnread: summary.latestUnread,
        loading,
        error,
        refreshSummary,
        markAsRead,
        markAsUnread,
        markAllAsRead,
    }), [summary, loading, error, refreshSummary, markAsRead, markAsUnread, markAllAsRead]);

    return (
        <FrontNotificationsContext.Provider value={value}>
            {children}
        </FrontNotificationsContext.Provider>
    );
}

export function useFrontNotifications(): FrontNotificationsContextValue {
    const context = useContext(FrontNotificationsContext);

    if (!context) {
        throw new Error('useFrontNotifications must be used within a FrontNotificationsProvider.');
    }

    return context;
}
